import React from 'react';
import FormWrapper from '../../../BaseModule/Components/Form/FormWrapper';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import SubmitButton from '../../../BaseModule/Components/Button/SubmitButton';
import { joinClasses } from '../../../../Utils/className.util';
import Text from '../../../BaseModule/Components/Text';
import styles from './UserWalletDeposit.module.scss';
import apiClient from '../../../../Services/ApiClient';
import useApiClient from '../../../../Hooks/useApiClient';
import { useAppContext } from '../../../../Services/AppContext';
import useLoading from '../../../Base/Hook/useLoading';
import InputField from '../../../BaseModule/Components/Form/Components/InputField';
import useTranslation from '../../../../Hooks/useTranslation';

const UserWalletDeposit: React.FC = () => {
	const { t } = useTranslation();
	const { displayError } = useApiClient();
	const { currency, meddiCreditsMinimumBuyIn } = useAppContext();
	const { isLoading, setIsLoading } = useLoading();

	const fieldName = t('component.userWalletDeposit.validationMessage');
	const requiredMessage = `${fieldName} ${t(
		'validation.form.isRequiredToFill',
	)}`;
	const minimumBuyInMessage = `${fieldName} ${t(
		'validation.form.mustBeAtLeast',
	)} ${meddiCreditsMinimumBuyIn}`;

	const validationSchema = yup.object({
		quantity: yup
			.number()
			.typeError(requiredMessage)
			.integer('validation.form.mustFillNumber')
			.required(requiredMessage)
			.test('is-minimum-buy-in', minimumBuyInMessage, value =>
				Number.isFinite(value)
					? Number(value) >= meddiCreditsMinimumBuyIn
					: true,
			),
	});

	const formMethods = useForm({
		defaultValues: {},
		resolver: yupResolver(validationSchema),
	});

	const handleDecimalKeyDown = (
		event: React.KeyboardEvent<HTMLInputElement>,
	) => {
		if ([',', '.', 'e', 'E', '+', '-'].includes(event.key)) {
			event.preventDefault();
		}
	};

	const handleSubmit = async (values: { quantity: number }) => {
		setIsLoading(true);
		try {
			const response = await apiClient.post('/meddi-credits', {
				quantity: values?.quantity,
			});

			if (response?.data?.redirectUrl) {
				window.location.href = response.data.redirectUrl;
				return;
			}
		} catch (e) {
			displayError(e);
		}
		setIsLoading(false);
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<InputField
				name='quantity'
				label='component.userWalletDeposit.form.creditsQuantity'
				isRequired
				step={1}
				min={meddiCreditsMinimumBuyIn}
				type='number'
				inputMode='numeric'
				onKeyDown={handleDecimalKeyDown}
			/>
			<Text
				tx='component.userWalletDeposit.form.hint'
				className={styles.hint}
				txParams={{ currency: currency }}
			/>
			<SubmitButton
				onSubmit={handleSubmit}
				className={joinClasses(['btn btn-primary w-100 mt-3'])}
				title='component.userWalletDeposit.form.buy'
				disabled={isLoading}
			/>
		</FormWrapper>
	);
};

export default UserWalletDeposit;
